import { TenantEnum } from '@jsmdg/tenancy';
import {
    type ShippingValues,
    type ShippingValuesFields,
    type ShippingValuesPerTenant,
} from '../client/types/shippingValueTypes';
import { type SharedData, type TenantConfig } from './types';

const emptySharedData: SharedData = {
    serviceHours: {},
    urls: {
        b2bWebsite: '',
        payback: '',
    },
    contactInfo: {
        phone: {
            label: '',
            number: '',
        },
        b2cPhoneNumber: '',
        b2bPhoneNumber: '',
        address: {
            street: '',
            zipCode: '',
            city: '',
        },
        serviceEmail: '',
    },
    brandDisplayName: '',
};

const JS_DEFAULT = {
    paybackIsEnabled: true,
    nonPaybackSkus: [
        '6143', // book
        '6473', // audio book
        '7608', // audio book
        '7496', // book
    ],
    websiteMarkup: {
        sellerName: 'Jochen Schweizer GmbH',
    },
    shareTitle: 'Jochen Schweizer',
    shippingModalDescription: 'Erlebnisauswahl in der hochwertigen Geschenkbox inkl. 1 Gutschein',
    b2bTeam: 'Jochen Schweizer Firmenkunden-Team',
    b2bWebsiteText: 'www.b2b.jochen-schweizer.de/',
    websiteText: 'www.jochen-schweizer.de',
    redeemText: '3 Jahre Gültigkeit',
    uspPrimaryText: 'Beste Auswahl an einzigartigen Erlebnissen',
    uspSecondaryText: 'Der Marktführer mit über 1 Million Kunden',
    uspTertiaryText: 'Flexible Gutscheine für Zeit, Ort und Erlebnis',
    friendsRabattText: '-10% ab dem 2. Gutschein',
    ...emptySharedData,
};

const MD_DEFAULT = {
    ...JS_DEFAULT,
    nonPaybackSkus: [],
    websiteMarkup: {
        sellerName: 'mydays GmbH',
    },
    shareTitle: 'mydays',
    b2bTeam: 'mydays Firmenkunden-Team',
    b2bWebsiteText: 'www.b2b.mydays.de/',
    websiteText: 'www.mydays.de',
    redeemText: '3 Jahre Gültigkeit',
    uspPrimaryText: 'Mehr als 8.000 ausgewählte Erlebnisse',
    uspSecondaryText: '20 Jahre Erlebnisgeschenk-Experte',
    uspTertiaryText: 'Flexible Gutscheine für Zeit, Ort und Erlebnis',
};

const tenantConfigMapping: Record<string, TenantConfig> = {
    [TenantEnum.JS_DE]: JS_DEFAULT,
    [TenantEnum.JS_AT]: {
        ...JS_DEFAULT,
        websiteText: 'www.jochen-schweizer.at',
        redeemText: '10 Jahre Gültigkeit',
        paybackIsEnabled: false,
        nonPaybackSkus: [],
    },
    [TenantEnum.MD_DE]: MD_DEFAULT,
    [TenantEnum.MD_AT]: {
        ...MD_DEFAULT,
        websiteText: 'www.mydays.at',
        redeemText: '10 Jahre Gültigkeit',
        paybackIsEnabled: false,
    },
    [TenantEnum.MD_CH]: {
        ...MD_DEFAULT,
        websiteText: 'www.mydays.ch',
        redeemText: '10 Jahre Gültigkeit',
        paybackIsEnabled: false,
    },
};

const ticketsAndVouchersDE: ShippingValuesFields = {
    shipMethodStar: '',
    minShipDays: 1,
    maxShipDays: 3,
    standardShip: 4.9,
    expressShip: 16,
    giftShip: 7.9,
};

const erlebnisBoxDE: ShippingValuesFields = {
    shipMethodStar: '',
    minShipDays: 1,
    maxShipDays: 3,
    standardShip: 4.9,
    expressShip: 16,
};

const ticketsAndVouchersAT: ShippingValuesFields = {
    shipMethodStar: '*',
    minShipDays: 3,
    maxShipDays: 4,
    standardShip: 4.9,
    giftShip: 7.9,
    additionalCost: 2,
};

const erlebnisBoxAT: ShippingValuesFields = {
    shipMethodStar: '*',
    minShipDays: 3,
    maxShipDays: 4,
    standardShip: 4.9,
};

const giftShippingBoxDE: ShippingValuesFields = {
    ...erlebnisBoxDE,
};

const giftShippingBoxAT: ShippingValuesFields = {
    shipMethodStar: '*',
    minShipDays: 3,
    maxShipDays: 4,
    standardShip: 4.9,
};

const SHIPPING_VALUES_PER_TENANT: ShippingValuesPerTenant = {
    JS_DE: {
        ticketsAndVouchers: ticketsAndVouchersDE,
        erlebnisBox: erlebnisBoxDE,
        currency: 'EUR',
    },
    JS_AT: {
        ticketsAndVouchers: ticketsAndVouchersAT,
        erlebnisBox: erlebnisBoxAT,
        currency: 'EUR',
    },
    MD_DE: {
        ticketsAndVouchers: { ...ticketsAndVouchersDE, standardShip: 4.9 },
        erlebnisBox: { ...erlebnisBoxDE, standardShip: 4.9 },
        giftShippingBox: giftShippingBoxDE,
        currency: 'EUR',
    },
    MD_AT: {
        ticketsAndVouchers: {
            ...ticketsAndVouchersAT,
            shipMethodStar: '',
        },
        erlebnisBox: {
            ...erlebnisBoxAT,
            shipMethodStar: '',
        },
        giftShippingBox: {
            ...giftShippingBoxAT,
            shipMethodStar: '',
        },
        currency: 'EUR',
    },
    MD_CH: {
        ticketsAndVouchers: {
            ...ticketsAndVouchersAT,
            shipMethodStar: '',
            giftShip: 7.9,
        },
        erlebnisBox: {
            ...erlebnisBoxAT,
            shipMethodStar: '',
            expressShip: undefined,
        },
        giftShippingBox: {
            ...giftShippingBoxAT,
            shipMethodStar: '',
            expressShip: undefined,
        },
        currency: 'CHF',
    },
};

function getShippingValuesFromTenant(tenant: keyof ShippingValuesPerTenant): ShippingValues {
    return SHIPPING_VALUES_PER_TENANT[tenant] || { ticketsAndVouchers: {}, erlebnisBox: {} };
}

const getTenantConfig = (tenant: TenantEnum): TenantConfig => {
    return tenantConfigMapping[tenant] || tenantConfigMapping[TenantEnum.JS_DE];
};

export { emptySharedData, getShippingValuesFromTenant, getTenantConfig };
